import { useEffect, useState } from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import "@/App.css";
import AboutSection from "@/components/AboutSection";
import AcademySection from "@/components/AcademySection";
import BookingModal from "@/components/BookingModal";
import Footer from "@/components/Footer";
import HeroSection from "@/components/HeroSection";
import Navigation from "@/components/Navigation";
import PracticeSection from "@/components/PracticeSection";
import AboutPage from "@/pages/AboutPage";
import AdminLogin from "@/pages/admin/Login";
import AdminLayout from "@/layouts/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminCalendar from "@/pages/admin/Calendar";
import AdminPatients from "@/pages/admin/Patients";
import PatientProfile from "@/pages/admin/PatientProfile";
import AdminFinance from "@/pages/admin/Finance";
import AdminBookings from "@/pages/admin/Bookings";
import AdminProviders from "@/pages/admin/providers/Providers";
import ProviderForm from "@/pages/admin/providers/ProviderForm";
import ProviderProfile from "@/pages/admin/providers/ProviderProfile";
import AdminSettings from "@/pages/admin/Settings";
import AdminTeam from "@/pages/admin/Team";
import ScheduleIndex from "@/pages/admin/schedule/ScheduleIndex";
import ProviderSchedule from "@/pages/admin/schedule/ProviderSchedule";
import ServicesIndex from "@/pages/admin/services/ServicesIndex";
import ServiceForm from "@/pages/admin/services/ServiceForm";
import LDACourses from "@/pages/admin/lda/Courses";
import LDAStudents from "@/pages/admin/lda/LDAStudents";
import LDAMaterials from "@/pages/admin/lda/LDAMaterials";
import LDAAnalytics from "@/pages/admin/lda/LDAAnalytics";

const HomePage = ({ onBook }) => (
  <>
    <HeroSection onBook={onBook} />
    <PracticeSection onBook={onBook} />
    <AboutSection onBook={onBook} />
    <AcademySection onBook={onBook} />
  </>
);

function AppInner() {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingAudience, setBookingAudience] = useState(null);

  const openBooking = (audience = null) => {
    setBookingAudience(audience);
    setBookingOpen(true);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        }),
      { threshold: 0.12 },
    );

    const revealElements = document.querySelectorAll(".reveal");
    revealElements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="App">
      <Routes>
        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="bookings" element={<AdminBookings />} />
          <Route path="calendar" element={<AdminCalendar />} />
          <Route path="schedule" element={<ScheduleIndex />} />
          <Route path="schedule/:id" element={<ProviderSchedule />} />
          <Route path="services" element={<ServicesIndex />} />
          <Route path="services/new" element={<ServiceForm />} />
          <Route path="services/:id/edit" element={<ServiceForm />} />
          <Route path="providers" element={<AdminProviders />} />
          <Route path="providers/new" element={<ProviderForm />} />
          <Route path="providers/:id" element={<ProviderProfile />} />
          <Route path="providers/:id/edit" element={<ProviderForm />} />
          <Route path="patients" element={<AdminPatients />} />
          <Route path="patients/:id" element={<PatientProfile />} />
          <Route path="delegates" element={<LDAStudents />} />
          <Route path="courses" element={<LDACourses />} />
          <Route path="materials" element={<LDAMaterials />} />
          <Route path="payments" element={<AdminFinance />} />
          <Route path="analytics" element={<LDAAnalytics />} />
          <Route path="team" element={<AdminTeam />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>

        {/* Public Routes */}
        <Route path="/*" element={
          <>
            <Navigation onBook={openBooking} />
            <Routes>
              <Route path="/" element={<HomePage onBook={openBooking} />} />
              <Route path="/about" element={<AboutPage onBook={openBooking} />} />
            </Routes>
            <Footer onBook={openBooking} />
            <BookingModal
              open={bookingOpen}
              onClose={() => setBookingOpen(false)}
              initialAudience={bookingAudience}
            />
          </>
        } />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}

export default App;
